import { permissionManifest } from "@msuite/citadel";
import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { DataTable } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type RolePermissionFormSchema, rolePermissionFormSchema } from "@msuite/utils/schemas";
import type { RoleDetail } from "@msuite/utils/types";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { canEditPermissions } from "@/components/citadel";
import { client } from "@/hono";
import { useFormAutosave } from "@/hooks/use-form-autosave";
import { customGrantKeys, toGrantDefaults, withoutCustomKeys } from "./grants";
import { createColumns } from "./table-config";

/** Props Interface */
interface PermissionEditorProps {
	roleId: string;
	role: RoleDetail;
}

export function PermissionEditor({ roleId, role }: PermissionEditorProps) {
	/** Context */
	const { t, language } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();

	/** Form */
	// The role is already on screen — the page fetched it, and this editor only mounts once it
	// resolved. Seeding through `values` rather than async `defaultValues` matters twice: it
	// spares a duplicate GET per page load, and it re-seeds the cells from the same prop the
	// `Custom` markers below read, so an invalidation cannot refresh the markers while the
	// cells keep pre-write grants.
	const values = useMemo(() => ({ grants: toGrantDefaults(role.permissions) }), [role.permissions]);
	const methods = useForm<RolePermissionFormSchema>({
		resolver: resolver(rolePermissionFormSchema),
		defaultValues: { grants: {} },
		values,
		// A reseed that lands while an edit's save is still in flight keeps the edit: the dirty
		// cells survive the reset and are exactly what the queued follow-up save submits. The
		// clean-up reset in `handleOnSubmit` clears the flags once nothing is pending, so later
		// reseeds reach every cell again.
		resetOptions: { keepDirtyValues: true },
	});

	/** Functions */
	async function handleOnSubmit(json: RolePermissionFormSchema) {
		try {
			const response = await client.roles[":roleId"].permissions.$patch({
				param: { roleId },
				json: { grants: withoutCustomKeys(json.grants, customKeys) },
			});
			await responseHandler(response, false);
			// One message per outcome, raised here rather than left to the handler — the grid
			// saves on selection, so the toast is what says the pick reached the server.
			toast.success(t("permissions.successfully_updated"));
			await qc.invalidateQueries({ predicate: invalidate(["roles", "citadel"]) });
			// The burst's last save settles the form. `json` is the whole form, not the payload —
			// the read-only keys are stripped on the way to the server only, so resetting to it
			// leaves the `Custom` cells with the value they render from.
			if (!autosave.hasQueuedSave()) methods.reset(json);
		} catch (error) {
			// A cell showing a reach the server refused would be a lie, and with saves happening
			// on selection there is no pending state to hold for a retry — the grid returns to
			// the last seeded truth, and the next selection is the retry.
			toast.error(error instanceof Error ? error.message : t("permissions.failed_to_update"));
			methods.reset(values);
		}
	}

	const onSubmit = methods.handleSubmit(handleOnSubmit);
	const autosave = useFormAutosave(methods, onSubmit);

	/** Computed */
	// The one grid an organisation cannot change — `admin` only; `member`'s grid stays editable,
	// it is where the base permission set is maintained. The server refuses the write with a
	// 403; the editor says so before the user spends a click on it.
	const isReadOnly = !canEditPermissions(role);
	const customKeys = useMemo(() => customGrantKeys(role.permissions), [role.permissions]);
	// `language` is a dependency although the callback does not read it: the column headers are
	// rendered through `localized(...)` at build time, so a language switch has to rebuild them —
	// the same reason the user grid keys its columns on the language.
	const columns = useMemo(
		() => createColumns({ customKeys, isDisabled: isReadOnly }),
		[customKeys, isReadOnly, language],
	);
	const data = useMemo(
		() => permissionManifest.map((entry) => ({ ...entry, id: entry.subject })),
		[],
	);

	/** Guard */
	// `admin` gets a sentence instead of a grid. Its one rule is `manage all` — every permission
	// including the ones added by future releases — and that is not an entry in the grid, which
	// only knows action-and-subject pairs. Rendered as a table it therefore came out as "no
	// access" in every cell: the exact opposite of the truth, about the one role that holds
	// everything.
	if (isReadOnly) {
		return (
			<ContentCard
				title={t("permissions.permissions")}
				icon={icon("Lock")}
			>
				<p className="label">{t("roles.admin_not_editable")}</p>
			</ContentCard>
		);
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<DataTable
				data={data}
				columns={columns}
				persistenceKey="role-permissions-table"
				initialPinningState={{ start: ["subject"] }}
				// Nothing here loads: the rows are the manifest, and the grants arrive with the role
				// the page already holds.
				isLoading={false}
				// No refresh control: the rows are a client-side constant and the values live in the
				// form, so there is nothing to refetch — and a refetch mid-edit would discard the
				// edits. Passing a no-op instead rendered the button, whose spinner asks
				// `isFetching({ queryKey })`; with the empty key that table used, that is a prefix
				// matching every query in the app, so it span whenever anything anywhere loaded.
				refetch={undefined}
				queryKey={undefined}
				menu={undefined}
			/>
		</FormProvider>
	);
}
